import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { CustomViewColumn } from 'src/app/shared/components/odata-filter/models/custom-view-column';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { SortDirection } from '@angular/material/sort';
import { CustomView } from 'src/app/shared/components/odata-filter/models/custom-view';
import { Subscription } from 'rxjs';
import { AdminCredentialsService } from 'src/app/admin/admin-home/admin-credentials/admin-credentials.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CredentialSingletonService } from '../../services/credential-singleton.service';
import { PersonCredentialApplicationReportModel } from './person-credential-application-report.model';
import { PersonCredentialApplicationReportService } from './person-credential-application-report.service';
import { ViewContext } from '../odata-filter/models/view-context';
import { ExportRequest } from '../odata-filter/models/export-request';
import { CredentialApplicationDefinitionModel } from '../../models/credential/credential-application-definition.model';
import { ScenarioRequirementStatus } from '../../models/credential/scenario-requirement-status.model';
import { TeamCredentialApplicationReportModel } from './team-credential-application-report.model';
import { TeamCredentialApplicationReportService } from './team-credential-application-report.service';
import { BaseCredentialApplicationReportModel } from './base-credential-application-report.model';
import { WorkspaceService } from '../../services/workspace.service';
import { TeamCredentialApplicationReportFields } from './team-credential-application-report.fields';
import { PersonCredentialApplicationReportFields } from './person-credential-application-report.fields';
import { BaseCredentialApplicationReportFields } from './base-credential-application-report.fields';

@Component({
  selector: 'app-credential-application-report',
  templateUrl: './credential-application-report.component.html',
  styleUrls: ['./credential-application-report.component.scss'],
  standalone: false
})
export class CredentialApplicationReportComponent implements OnInit, OnDestroy {
  title: string = '';
  dataSource = new MatTableDataSource();
  appliedFilters: SelectedFilter[] = [];
  report: (PersonCredentialApplicationReportModel | TeamCredentialApplicationReportModel)[] = [];
  notInWorkspaceIds = new Set<string>();
  sortColumnName: TeamCredentialApplicationReportFields | PersonCredentialApplicationReportFields =
    PersonCredentialApplicationReportFields.firstName;
  orderDirection: SortDirection = 'asc';
  customViewColumns: CustomViewColumn[] = [];
  credentialApplication: CredentialApplicationDefinitionModel;
  subscriptions: Subscription[] = [];
  isDrawerOpen: boolean = false;
  viewContextId: string;
  viewContext: ViewContext;
  credentialId: string;
  reportConfigured: boolean = false;
  filterService: PersonCredentialApplicationReportService | TeamCredentialApplicationReportService;

  constructor(
    private personFilterService: PersonCredentialApplicationReportService,
    private teamFilterService: TeamCredentialApplicationReportService,
    private adminCredentialsService: AdminCredentialsService,
    private credentialSingletonService: CredentialSingletonService,
    private route: ActivatedRoute,
    private router: Router,
    private workspaceService: WorkspaceService
  ) {}

  async ngOnInit(): Promise<void> {
    this.viewContext = this.route.snapshot.data['viewContext'];
    let applicationId = this.route.snapshot.params['applicationId'];
    this.credentialId = this.route.snapshot.params['credentialId'];
    this.viewContextId = applicationId || this.credentialId;
    let viewId = this.route.snapshot.queryParams['viewId'];

    this.filterService = this.isTeamReport() ? this.teamFilterService : this.personFilterService;

    this.subscriptions.push(
      this.filterService.reportConfigured.subscribe(() => {
        this.reportConfigured = true;
        if (this.selectedView) {
          this.makeDynamicTable();
          this.filterService.applyView(this.selectedView);
        }
      }),
      this.filterService.filterResultValues.subscribe((report) => {
        if (!report) return;
        this.calculateScenarioCompletion(report);
        this.notInWorkspaceIds = new Set(
          report
            .filter((row) => !this.workspaceService.isCurrentWorkspace(row.workspaceName))
            .map((row) => row.id)
        );
        this.report = report;
        this.dataSource.data = report;
      }),

      this.filterService.appliedFilters.subscribe((appliedFilters) => {
        if (!appliedFilters) return;
        this.appliedFilters = appliedFilters;
      }),
      this.credentialSingletonService.entityCredentialScenarioDeleted.subscribe((scenarioId) => {
        if (!scenarioId) return;
        this.report = this.report.filter((x) => x.id != scenarioId);
        this.dataSource.data = this.report;
      }),
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.toggleDetailsSidebar();
      })
    );

    // Set initial sort order based on report type
    if (this.viewContext == ViewContext.TeamCredentialApplicationReport) {
      this.sortColumnName = TeamCredentialApplicationReportFields.teamName;
      this.orderBy = [TeamCredentialApplicationReportFields.teamName];
    } else {
      this.sortColumnName = PersonCredentialApplicationReportFields.firstName;
      this.orderBy = [PersonCredentialApplicationReportFields.firstName];
    }

    this.toggleDetailsSidebar();

    if (
      (this.viewContext == ViewContext.PersonCredentialApplicationReport ||
        this.viewContext == ViewContext.TeamCredentialApplicationReport) &&
      this.credentialId
    ) {
      const credentials = await this.adminCredentialsService.getAllCredentialsNames();
      const credential = credentials.find((c) => c.id === this.credentialId);
      this.title = credential
        ? `${credential.name} / All Credential Applications`
        : 'All Credential Applications';
    } else {
      this.title = 'All Credential Applications';
    }

    await this.filterService.setApplicationId(
      viewId,
      this.credentialApplication?.id,
      this.credentialId,
      this.viewContext
    );
  }

  get orderBy() {
    return this.filterService.orderBy;
  }
  set orderBy(value) {
    this.filterService.orderBy = value;
  }
  get displayedColumns() {
    let dColumns = this.customViewColumns
      .sort((n1, n2) => n1.rank - n2.rank)
      .map((x) => x.columnDefinition);
    return dColumns;
  }

  get selectedView(): CustomView {
    return this.filterService.selectedView;
  }

  get isFetchingData(): boolean {
    return this.filterService.isFetchingData;
  }

  get totalResultCount(): number {
    return this.filterService.totalResultCount;
  }

  onSortChange(event) {
    this.sortColumnName = event.active;

    this.orderDirection = event.direction;
    this.orderBy = [`${this.sortColumnName} ${this.orderDirection}`];
    this.filterService.applyFilter(this.appliedFilters);
  }

  columnsChanged(columns: CustomViewColumn[]) {
    this.makeDynamicTable(columns);
    this.selectedView.isModified = true;
    this.selectedView.columns = columns;
    this.filterService.isFetchingData = true;
    this.filterService.applyFilter(this.appliedFilters);
  }

  selectView(view: CustomView) {
    if (view && this.reportConfigured) {
      this.makeDynamicTable();
      this.filterService.applyView(view);
    }
  }
  getDynamicScenarioColumnValue(id: string, element: PersonCredentialApplicationReportModel) {
    let scenario = element.scenarios.find((x) => x.scenarioDefinitionId == id);
    if (!scenario) return '---';
    return `${scenario.scenarioCompletion}%`;
  }

  private *defaultColumns(): Generator<CustomViewColumn> {
    let columnIndex = 0;
    yield CustomViewColumn.createSystemColumn(
      BaseCredentialApplicationReportFields.credentialApplicationNumber,
      'Application Number',
      columnIndex++
    );

    if (this.isTeamReport()) {
      yield CustomViewColumn.createSystemColumn(
        TeamCredentialApplicationReportFields.teamName,
        'Team Name',
        columnIndex++
      );
      yield CustomViewColumn.createSystemColumn(
        TeamCredentialApplicationReportFields.teamPublicName,
        'Team Public Name',
        columnIndex++
      );
      yield CustomViewColumn.createSystemColumn(
        TeamCredentialApplicationReportFields.workspaceName,
        'Workspace Name',
        columnIndex++
      );
    } else {
      yield CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.firstName,
        'First Name',
        columnIndex++
      );
      yield CustomViewColumn.createSystemColumn(
        PersonCredentialApplicationReportFields.lastName,
        'Last Name',
        columnIndex++
      );
    }

    if (
      this.viewContext == ViewContext.PersonScenarioReport ||
      this.viewContext == ViewContext.TeamScenarioReport
    ) {
      yield CustomViewColumn.createSystemColumn(
        BaseCredentialApplicationReportFields.systemStatus,
        'Status',
        columnIndex++
      );
    } else if (
      this.viewContext == ViewContext.PersonCredentialApplicationReport ||
      this.viewContext == ViewContext.TeamCredentialApplicationReport
    ) {
      if (!this.credentialId) {
        yield CustomViewColumn.createSystemColumn(
          BaseCredentialApplicationReportFields.credentialName,
          'Credential Name',
          columnIndex++
        );
      }

      yield CustomViewColumn.createSystemColumn(
        BaseCredentialApplicationReportFields.systemStatus,
        'Application Status',
        columnIndex++
      );
      yield CustomViewColumn.createSystemColumn(
        BaseCredentialApplicationReportFields.applicationStatusLabel,
        'Application Status Label',
        columnIndex++
      );

      yield CustomViewColumn.createSystemColumn(
        BaseCredentialApplicationReportFields.renewalApplicationStatus,
        'Renewal Application Status',
        columnIndex++
      );
      yield CustomViewColumn.createSystemColumn(
        BaseCredentialApplicationReportFields.reinstateApplicationStatus,
        'Reinstate Application status',
        columnIndex++
      );
    } else {
      const scenarioColumns =
        this.credentialApplication?.scenarios
          .filter((x) => x.isActive)
          .map((scenario) => {
            let column = new CustomViewColumn();
            column.customFieldId = scenario.id;
            column.isCustomField = true;
            column.rank = columnIndex++;
            column.displayName = `${scenario.name} Scenario`;
            return column;
          }) || [];
      for (let scenarioColumn of scenarioColumns) {
        yield scenarioColumn;
      }
    }

    yield CustomViewColumn.createSystemColumn(
      BaseCredentialApplicationReportFields.credentialApplicationType,
      'Application Type',
      columnIndex++
    );

    yield CustomViewColumn.createSystemColumn(
      BaseCredentialApplicationReportFields.credentialApplicationStartDate,
      'Application Start',
      columnIndex++
    );

    yield CustomViewColumn.createSystemColumn(
      BaseCredentialApplicationReportFields.credentialApplicationEndDate,
      'Application End',
      columnIndex++
    );

    yield CustomViewColumn.createSystemColumn(
      BaseCredentialApplicationReportFields.credentialApplicationUpdatedOn,
      'Last Updated',
      columnIndex++
    );
  }

  private makeDynamicTable(columns: CustomViewColumn[] = []) {
    this.customViewColumns = columns?.length
      ? columns.map((data) => CustomViewColumn.parse(data))
      : Array.from(this.defaultColumns());
  }

  openSideBar(obj: BaseCredentialApplicationReportModel) {
    if (this.isTeamReport() && obj instanceof TeamCredentialApplicationReportModel) {
      if (!this.workspaceService.isCurrentWorkspace(obj.workspaceName)) {
        return;
      }
      this.router.navigate(['details', obj.teamId, obj.id], { relativeTo: this.route });
    }
    if (obj instanceof PersonCredentialApplicationReportModel) {
      this.router.navigate(['details', obj.personId, obj.id], { relativeTo: this.route });
    }
  }

  closeSideBar() {
    this.router.navigate(['.'], { relativeTo: this.route });
  }

  backToCredentialing() {
    this.router.navigate(['/reports/credentialing']);
  }

  getExportRequest = (isCustomExport: boolean): ExportRequest => {
    if (isCustomExport) {
      return {
        oDataQuery: this.filterService.getQueryWithNewSelectAndExpand(['id']),
        columns: []
      };
    }
    return {
      oDataQuery: this.filterService.currentODataFilterQuery,
      columns: this.customViewColumns
    };
  };

  private toggleDetailsSidebar() {
    if (this.route.firstChild && this.route.firstChild.snapshot.url[0].path == 'details') {
      this.isDrawerOpen = true;
    } else {
      this.isDrawerOpen = false;
    }
  }

  private calculateScenarioCompletion(
    report: PersonCredentialApplicationReportModel[] | TeamCredentialApplicationReportModel[]
  ): void {
    report.forEach((item) => {
      item.scenarios.forEach((scenario) => {
        const requirements = scenario.requirements || [];
        const totalRequirements = requirements.length;
        const completedRequirements = requirements.filter(
          (req) => req.status === ScenarioRequirementStatus.Complete
        ).length;
        scenario.scenarioCompletion =
          totalRequirements > 0 ? Math.floor((completedRequirements / totalRequirements) * 100) : 0;
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public isTeamReport = (): boolean =>
    this.viewContext == ViewContext.TeamCredentialApplicationReport;
}
